import { skipToken } from '@reduxjs/toolkit/query';
import { shallowEqual, useSelector } from 'react-redux';

import type { RootState } from '../../../configuration/setup/store';
import { getOptionalAccountId } from '../../../configuration/tokenHandling/tokenSlice';
import type { PaymentMethodRequirementRequest } from '../api/paymentMethodRequirements/paymentMethodRequirement.types';
import {
    paymentMethodRequirementApiSlice,
    useGetPaymentMethodRequirementQuery,
} from '../api/paymentMethodRequirements/paymentMethodRequirementApiSlice';
import { isServiceCareLBeta } from '../common/betaService';
import { isRatePlanBookable } from '../common/product/productService';
import { isManNowProduct } from '../config/manNowProducts';
import {
    getLevelOfProduct,
    getSelectedProduct,
    getSelectedProductId,
    getSelectedProductLevel,
} from './redux/checkout.redux';
import { isBookableProduct, isMultiLevelProduct, type Product } from './redux/types';
import { getSelectedResourceIds } from './resourceSelection/redux/resourceSelection.redux';

export const getPaymentMethodRequirementRequest = (state: RootState): PaymentMethodRequirementRequest | undefined => {
    const accountId = getOptionalAccountId(state);
    const productId = getSelectedProductId(state);
    const resourceIds = getSelectedResourceIds(state);
    const level = getSelectedProductLevel(state);

    if (!accountId || !productId || resourceIds.length === 0) {
        return;
    }

    return {
        accountId,
        productId,
        level,
        resourceIds,
    };
};

export const usePaymentMethodRequirement = () => {
    const request = useSelector(getPaymentMethodRequirementRequest, shallowEqual);
    useGetPaymentMethodRequirementQuery(request ?? skipToken);
};

export const isPurchaseFree = (state: RootState): boolean => {
    const request = getPaymentMethodRequirementRequest(state);
    if (!request) {
        return false;
    }

    return (
        paymentMethodRequirementApiSlice.endpoints.getPaymentMethodRequirement.select(request)(state).data
            ?.paymentMethodRequired === false
    );
};

export const getCurrencyOfSelectedProduct = (state: RootState): string | undefined => {
    const product = getSelectedProduct(state);
    if (isBookableProduct(product)) {
        return product.currency;
    }
    if (isMultiLevelProduct(product)) {
        return getLevelOfProduct(state)?.currency;
    }
    return;
};

function isProductLevelBookable(selectedProduct: Product, selectedProductLevel: string) {
    if (isBookableProduct(selectedProduct) && selectedProductLevel === selectedProduct.level) {
        return true;
    }

    if (isMultiLevelProduct(selectedProduct)) {
        const selectedRatePlan = selectedProduct.levels.find(ratePlan => ratePlan.level === selectedProductLevel);

        return (
            selectedRatePlan !== undefined &&
            isRatePlanBookable(selectedRatePlan) &&
            !isServiceCareLBeta(selectedProduct.sku, selectedProductLevel)
        );
    }

    return false;
}

export const isSelectedProductBookable = (state: RootState): boolean => {
    const selectedProductId = getSelectedProductId(state);
    const selectedProductLevel = getSelectedProductLevel(state);
    const selectedProduct = getSelectedProduct(state);

    if (!selectedProductId || !selectedProduct || isManNowProduct(selectedProductId)) {
        return false;
    }
    if (selectedProductLevel) {
        return isProductLevelBookable(selectedProduct, selectedProductLevel);
    }
    return isBookableProduct(selectedProduct) && !selectedProduct.level && !isMultiLevelProduct(selectedProduct);
};
